'use client';
import { Instagram, Facebook } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useT, useI18n } from '../../i18n';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const t = useT();
  const { locale } = useI18n();
  const pathname = usePathname() || '/';
  // 條款及細則單一入口（/terms/ 三 TAB：隱私／退款／交付）；zh-HK 無前綴
  const termsHref = locale === 'zh-HK' ? '/terms/' : `/terms/${locale}/`;
  // 品牌 logo 返主頁；子頁上 hash 連結跳返主頁對應 section（URL 形狀鐵律：首頁 locale 前綴）
  const homeHref = locale === 'zh-HK' ? '/' : `/${locale}/`;
  const onHome = pathname.replace(/\/+$/, '') === (locale === 'zh-HK' ? '' : '/' + locale);
  const withHome = (hash: string) => (onHome ? hash : homeHref + hash);

  const footerLinks = {
    products: [
      { name: t('nav.arsenal'), href: '#arsenal' },
      { name: t('nav.components'), href: '#components' },
      { name: t('nav.custom'), href: '#custom' },
    ],
    content: [
      { name: t('nav.philosophy'), href: '#philosophy' },
    ],
    about: [
      { name: t('footer.aboutUs'), href: '#' },
      { name: t('footer.contact'), href: '#contact' },
      { name: t('footer.faq'), href: '#' },
    ],
  };

  // ===========================================
  // SOCIAL MEDIA LINKS CONFIGURATION
  // ===========================================
  // To set up your social media links, update the URLs below:
  // 
  // Facebook: Replace 'https://facebook.com/jkdcoding' with your Facebook page URL
  // Instagram: Replace 'https://instagram.com/jkdcoding' with your Instagram profile URL
  //
  // Example:
  // { icon: <Instagram />, href: 'https://instagram.com/your_username', label: 'Instagram' },
  // { icon: <Facebook />, href: 'https://facebook.com/yourpage', label: 'Facebook' },
  // ===========================================
  const socialLinks = [
    { 
      icon: <Instagram className="w-5 h-5" />, 
      href: 'https://instagram.com/jkdcoding', // <-- UPDATE YOUR INSTAGRAM URL HERE
      label: 'Instagram' 
    },
    { 
      icon: <Facebook className="w-5 h-5" />, 
      href: 'https://facebook.com/jkdcoding', // <-- UPDATE YOUR FACEBOOK URL HERE
      label: 'Facebook' 
    },
  ];

  return (
    <footer className="bg-jkd-black border-t border-jkd-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-4 lg:col-span-2">
            <a href={homeHref} className="inline-block mb-4">
              <span className="text-2xl font-black tracking-tighter">
                <span className="text-white">JKD</span>
                <span className="text-jkd-gold">Coding</span>
              </span>
            </a>
            <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-sm">
              {t('footer.desc')}
            </p>
            <div className="flex gap-4">
              {socialLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.label}
                  className="w-10 h-10 bg-jkd-gray-100 rounded flex items-center justify-center text-gray-400 hover:text-jkd-gold hover:bg-jkd-gray-200 transition-all duration-300"
                >
                  {link.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.products')}</h4>
            <ul className="space-y-3">
              {footerLinks.products.map((link, index) => (
                <li key={index}>
                  <a
                    href={withHome(link.href)}
                    className="text-gray-400 hover:text-jkd-gold transition-colors text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Content */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.content')}</h4>
            <ul className="space-y-3">
              {footerLinks.content.map((link, index) => (
                <li key={index}>
                  <a
                    href={withHome(link.href)}
                    className="text-gray-400 hover:text-jkd-gold transition-colors text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* About */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.about')}</h4>
            <ul className="space-y-3">
              {footerLinks.about.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href === '#' ? link.href : withHome(link.href)}
                    className="text-gray-400 hover:text-jkd-gold transition-colors text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-jkd-gray-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              © {currentYear} JKD Coding. {t('footer.rights')}
            </p>
            <div className="flex items-center gap-6 text-sm">
              <a href={termsHref} className="text-gray-500 hover:text-gray-400 transition-colors">
                {t('footer.termsLink')}
              </a>
            </div>
          </div>
        </div>

        {/* Water Mark */}
        <div className="mt-8 text-center">
          <p className="text-jkd-gold/20 text-xs font-mono tracking-widest">
            BE WATER, BE CODE
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
